import { eroiIds, TvdValuePillar } from "./tvd-types";

/**
 * Legacy composite-tile lookup used by `isSubComponentDataZeroOrEmpty` and
 * `getSubComponentsData` in the main component.
 *
 * Each entry maps a parent composite id to the child metric ids it aggregates.
 * Kept alongside the new config system for backward-compatibility with those helpers.
 *
 * ⚠️  If you add a new composite tile, add it here AND add a `CompositeTileConfig`
 * entry in the appropriate pillar config below.
 */
export const compositeTilesConfig = [
  {
    composite_tile_id: eroiIds.SOLID_WASTE,
    subcomponents: [
      eroiIds.PLASTIC,
      eroiIds.CARDBOARD
    ]
  },
  {
    composite_tile_id: eroiIds.GREENHOUSEGASES,
    subcomponents: [
      eroiIds.GREENHOUSEGASES_ENERGY,
      eroiIds.GREENHOUSEGASES_PACKAGING,
      eroiIds.GREENHOUSEGASES_TRANSPORT
    ]
  },
  {
    composite_tile_id: eroiIds.LABORPRODUCTIVITY,
    subcomponents: [
      eroiIds.NOCHARGEADDITIONALSERVICES,
      eroiIds.GENERALCONTRACTORCOSTAVOIDANCE,
      eroiIds.SETUPSERVICES,
      eroiIds.PESTPREVENTIONFINDINGS
    ]
  }
];

/**
 * Drives the TVD metric-summary pills at the bottom of the dashboard
 * (Water consumed, Energy saved, Solid Waste reduced).
 *
 * Each entry groups one or more metric ids under a display group id so the
 * `tvd-metric-summary` component can aggregate values from multiple metrics
 * into a single pill (e.g. ENERGY includes both Ecolab energy AND furniture-
 * preservation energy).
 */
export const tvdSummaryPillsConfig = [
  {
    metricGroupId: eroiIds.WATER,
    metricSummaryComponents: [
      eroiIds.WATER
    ]
  },
  {
    metricGroupId: eroiIds.ENERGY,
    metricSummaryComponents: [
      eroiIds.ENERGY,
      eroiIds.FURNITUREPRESERVATIONENERGY
    ]
  },
  {
    metricGroupId: eroiIds.SOLID_WASTE,
    metricSummaryComponents: [
      eroiIds.PLASTIC,
      eroiIds.CARDBOARD
    ]
  }
]

/**
 * The complete set of boolean context flags that a Guard expression can reference.
 *
 * Why a named type instead of plain `string`?
 * Prevents typos — the compiler will reject an unknown flag name.
 *
 * | Flag                                | When true                                                     |
 * |-------------------------------------|---------------------------------------------------------------|
 * | isPestDivision                      | Division === 'Pest'                                           |
 * | enableTVDForAPInstitutional         | Feature flag allowing TVD for AP Institutional                |
 * | enableTVDForLAInstitutional         | Feature flag allowing TVD for LA Institutional                |
 * | enableTVDForIMEAInstitutional       | Feature flag allowing TVD for IMEA Institutional              |
 * | isRegionEU                          | Every selected region is EU                                   |
 * | isAPInstitutional                   | Region includes AP AND division === 'Institutional'           |
 * | isLAInstitutional                   | Region includes LA AND division === 'Institutional'           |
 * | isMultipleRegionsSelected           | More than one region is selected (global view)                |
 * | isAggregateGhgVisible               | Compound: isMultipleRegionsSelected OR !isRegionEU.           |
 * |                                     | True for all regions except single-region EU (which           |
 * |                                     | uses the composite GHG breakdown tile instead).               |
 * | isAPOrLAOrIMEAInstitutionalEnabled  | Compound: (isAPInstitutional AND enableTVDForAPInstitutional) |
 * |                                     | OR (isLAInstitutional AND enableTVDForLAInstitutional)        |
 * |                                     | OR (isIMEAInstitutional AND enableTVDForIMEAInstitutional)    |
 * |                                     | Gates the GHG Energy tile for AP, LA, and IMEA.               |
 */
export type TvdGuardFlag =
  | 'isPestDivision'
  | 'enableTVDForAPInstitutional'
  | 'enableTVDForLAInstitutional'
  | 'enableTVDForIMEAInstitutional'
  | 'isRegionEU'
  | 'isAPInstitutional'
  | 'isLAInstitutional'
  | 'isIMEAInstitutional'
  | 'isMultipleRegionsSelected'
  | 'isAggregateGhgVisible'
  | 'isAPOrLAOrIMEAInstitutionalEnabled';

/**
 * Declarative visibility rule attached to an individual tile or composite.
 *
 * Evaluation logic (see `passesGuard()` in the component):
 *   • ALL flags in `requireAllTrue`  must be `true`  in the current context.
 *   • ALL flags in `requireAllFalse` must be `false` in the current context.
 *   • If both arrays are empty / absent the guard is considered passing.
 *
 * Why declarative guards instead of template `*ngIf`?
 * Moving visibility rules into data makes them:
 *   - Easy to read and review in one place.
 *   - Safe to modify without touching HTML templates.
 *   - Testable in isolation by simply inspecting the config object.
 *
 * Example — show only for non-Pest divisions when AP Institutional is enabled:
 * ```ts
 * guard: { requireAllFalse: ['isPestDivision'], requireAllTrue: ['enableTVDForAPInstitutional','enableTVDForLAInstitutional'] }
 * ```
 */
export interface Guard {
  requireAllTrue?: TvdGuardFlag[];
  requireAllFalse?: TvdGuardFlag[];
}

/**
 * Discriminates between the two kinds of TVD tiles.
 *
 * `EROI`      — A single metric tile rendered by `<eroi-metric>`.
 * `COMPOSITE` — A parent tile rendered by `<composite-eroi-metric>` that groups
 *               several EROI children and displays their aggregate value.
 *
 * Used in `EroiTileConfig.type` and `CompositeTileConfig.type` so switch
 * statements and type narrowing work without magic strings.
 */
export enum TvdTileType {
  EROI = 'eroi',
  COMPOSITE = 'composite',
}

/**
 * Describes a single `<eroi-metric>` tile in the refreshed TVD UI.
 *
 * | Field          | Purpose                                                        |
 * |----------------|----------------------------------------------------------------|
 * | id             | HTML element id bound to `[id]` on the component.             |
 * | metricId       | The eroiIds value used to look up data via `getDataByMetricId`.|
 * | valuePillar    | Which pillar this tile belongs to (used for pillar children    |
 * |                | arrays and selection-tracking in the component).              |
 * | type           | Always `TvdTileType.EROI` — narrows the union type.           |
 * | dataKey        | Property name on the component holding the `tvdData` object.  |
 * |                | Kept for documentation; runtime lookup uses `metricId`.       |
 * | programKey     | The key checked against `getProgramKeys(tvdProgramScope)` to  |
 * |                | hide tiles when the active program tab doesn't include them.  |
 * | guard          | Optional visibility rules (see `Guard`).                       |
 * | isSubComponent | `true` when rendered inside a `<composite-eroi-metric>`.      |
 */
export interface EroiTileConfig {
  id: eroiIds;
  metricId: eroiIds;
  valuePillar: TvdValuePillar;
  type: TvdTileType.EROI;
  dataKey: string;
  programKey: string;
  guard?: Guard;
  isSubComponent?: boolean;
}

/**
 * Describes a `<composite-eroi-metric>` tile — a parent tile that aggregates
 * several child EROI metrics and displays their combined monetary/value totals.
 *
 * | Field                 | Purpose                                                       |
 * |-----------------------|---------------------------------------------------------------|
 * | id                    | Matches `composite_tile_id` in `compositeTilesConfig` so that |
 * |                       | `getSubComponentsData(id)` and `isSubComponentDataZeroOrEmpty`|
 * |                       | can resolve child data from the legacy lookup table.          |
 * | valuePillar           | Which pillar this composite belongs to.                       |
 * | type                  | Always `TvdTileType.COMPOSITE`.                               |
 * | guard                 | Optional visibility rules (see `Guard`).                      |
 * | showInfoTooltip       | Passed directly to `[showInfoTooltip]` on the component.      |
 * |                       | `true` for Labor Productivity (Pest), `false` for others.     |
 * |                       | If a tooltip is shown the `View Details` button is not shown  |
 * |                       | since the metric cannot be drilled into.                      |
 * | children              | The individual EROI tiles rendered inside this composite.     |
 * |                       | Child visibility is determined by data availability and the   |
 * |                       | active program key — NOT by a separate guard.                 |
 */
export interface CompositeTileConfig {
  id: eroiIds;
  valuePillar: TvdValuePillar;
  type: TvdTileType.COMPOSITE;
  guard?: Guard;
  showInfoTooltip: boolean;
  children: EroiTileConfig[];
}

// ── Business Outcomes Config ──────────────────────────────────────────────────
/**
 * Individual (EROI) tiles for the **Business Outcomes** value pillar.
 *
 * Currently contains only Employee Safety.
 * Adding a new tile here is all that is needed to render it in the refreshed UI —
 * no template changes required.
 *
 * Division coverage:
 * - Non-Pest divisions only (`requireAllFalse: ['isPestDivision']`).
 */
export const BusinessOutcomesTilesConfig: EroiTileConfig[] = [
  {
    id: eroiIds.EMPLOYEESAFETY,
    metricId: eroiIds.EMPLOYEESAFETY,
    valuePillar: TvdValuePillar.BusinessOutcomes,
    type: TvdTileType.EROI,
    dataKey: 'employeeSafetyData',
    programKey: 'employee-safety',
    guard: { requireAllFalse: ['isPestDivision'] },
  },
];

// ── Operational Performance Configs ──────────────────────────────────────────
/**
 * Individual (EROI) tiles for the **Operational Performance** value pillar.
 *
 * Contains two sets of tiles controlled by division guards:
 *
 * **Non-Pest** (Institutional, QSR, FRS, …):
 *   Productivity, Assets, Cost Avoidance, Portfolio Reduction.
 *
 * **Pest only**:
 *   Furniture Preservation Assets, Room Nights Saved.
 *   (The Labor Productivity composite sits in `OperationalPerformanceCompositeTilesConfig`.)
 *
 * To add a tile for a specific division, add an entry with the matching guard.
 * The component's `visibleOperationalPerformanceTiles()` will pick it up automatically.
 */
export const OperationalPerformanceTilesConfig: EroiTileConfig[] = [
  // Non-pest product metrics
  {
    id: eroiIds.PRODUCTIVITY,
    metricId: eroiIds.PRODUCTIVITY,
    valuePillar: TvdValuePillar.OperationalPerformance,
    type: TvdTileType.EROI,
    dataKey: 'productivityData',
    programKey: 'productivity',
    guard: { requireAllFalse: ['isPestDivision'] },
  },
  {
    id: eroiIds.ASSETS,
    metricId: eroiIds.ASSETS,
    valuePillar: TvdValuePillar.OperationalPerformance,
    type: TvdTileType.EROI,
    dataKey: 'assetsData',
    programKey: 'assets',
    guard: { requireAllFalse: ['isPestDivision'] },
  },
  {
    id: eroiIds.COSTAVOIDANCE,
    metricId: eroiIds.COSTAVOIDANCE,
    valuePillar: TvdValuePillar.OperationalPerformance,
    type: TvdTileType.EROI,
    dataKey: 'costAvoidData',
    programKey: 'cost-avoidance',
    guard: { requireAllFalse: ['isPestDivision'] },
  },
  {
    id: eroiIds.PORTFOLIO,
    metricId: eroiIds.PORTFOLIO,
    valuePillar: TvdValuePillar.OperationalPerformance,
    type: TvdTileType.EROI,
    dataKey: 'portfolioData',
    programKey: 'portfolio-reduction',
    guard: { requireAllFalse: ['isPestDivision'] },
  },
  // Pest-only standalone metrics
  {
    id: eroiIds.FURNITUREPRESERVATIONASSETS,
    metricId: eroiIds.FURNITUREPRESERVATIONASSETS,
    valuePillar: TvdValuePillar.OperationalPerformance,
    type: TvdTileType.EROI,
    dataKey: 'furniturePreservationAssetsData',
    programKey: 'furniture-preservation-assets',
    guard: { requireAllTrue: ['isPestDivision'] },
  },
  {
    id: eroiIds.ROOMNIGHTSSAVED,
    metricId: eroiIds.ROOMNIGHTSSAVED,
    valuePillar: TvdValuePillar.OperationalPerformance,
    type: TvdTileType.EROI,
    dataKey: 'roomNightsSavedData',
    programKey: 'room-nights-saved',
    guard: { requireAllTrue: ['isPestDivision'] },
  },
];

/**
 * Composite tiles for the **Operational Performance** value pillar.
 *
 * Currently contains only the **Labor Productivity** composite, which is
 * shown exclusively for the Pest division.
 *
 * Children (No Charge Additional Services, Setup Services, General Contractor
 * Cost Avoidance, Pest Prevention Findings) are grouped under this composite
 * and their individual data is summed by `<composite-eroi-metric>`.
 *
 * `showInfoTooltip: true` — an informational tooltip is displayed on this
 * composite to explain what Labor Productivity measures.
 */
export const OperationalPerformanceCompositeTilesConfig: CompositeTileConfig[] = [
  {
    id: eroiIds.LABORPRODUCTIVITY,
    valuePillar: TvdValuePillar.OperationalPerformance,
    type: TvdTileType.COMPOSITE,
    showInfoTooltip: true,
    guard: { requireAllTrue: ['isPestDivision'] },
    children: [
      {
        id: eroiIds.NOCHARGEADDITIONALSERVICES,
        metricId: eroiIds.NOCHARGEADDITIONALSERVICES,
        valuePillar: TvdValuePillar.OperationalPerformance,
        type: TvdTileType.EROI,
        dataKey: 'noChargeAdditionalServicesData',
        programKey: 'no-charge-additional-services',
        isSubComponent: true,
      },
      {
        id: eroiIds.SETUPSERVICES,
        metricId: eroiIds.SETUPSERVICES,
        valuePillar: TvdValuePillar.OperationalPerformance,
        type: TvdTileType.EROI,
        dataKey: 'setupServicesData',
        programKey: 'set-up-services',
        isSubComponent: true,
      },
      {
        id: eroiIds.GENERALCONTRACTORCOSTAVOIDANCE,
        metricId: eroiIds.GENERALCONTRACTORCOSTAVOIDANCE,
        valuePillar: TvdValuePillar.OperationalPerformance,
        type: TvdTileType.EROI,
        dataKey: 'generalContractorCostAvoidanceData',
        programKey: 'general-contractor-cost-avoidance',
        isSubComponent: true,
      },
      {
        id: eroiIds.PESTPREVENTIONFINDINGS,
        metricId: eroiIds.PESTPREVENTIONFINDINGS,
        valuePillar: TvdValuePillar.OperationalPerformance,
        type: TvdTileType.EROI,
        dataKey: 'pestPreventionFindingsData',
        programKey: 'pest-prevention-findings',
        isSubComponent: true,
      },
    ],
  },
];

// ── Environmental Impact Configs ──────────────────────────────────────────────
/**
 * Individual (EROI) tiles for the **Environmental Impact** value pillar.
 *
 * Division / region coverage:
 *
 * | Tile                    | When visible                                                 |
 * |-------------------------|--------------------------------------------------------------|
 * | Water                   | Non-Pest                                                     |
 * | Energy                  | Non-Pest                                                     |
 * | Greenhouse Gases (total)| Non-Pest AND (multi-region OR non-EU AND non-AP)             |
 * |                         | Uses `isAggregateGhgVisible` compound flag.                  |
 * | GHG Energy              | AP, LA and IMEA Institutional only, single region, non-EU    |
 * | Furniture Preservation  | Pest division only                                           |
 * | Energy                  |                                                              |
 *
 * Note: The EU composite (GHG breakdown into Energy/Transport/Packaging) lives
 * in `EnvironmentalImpactCompositeTilesConfig`, not here.
 */
export const EnvironmentalImpactTilesConfig: EroiTileConfig[] = [
  {
    id: eroiIds.WATER,
    metricId: eroiIds.WATER,
    valuePillar: TvdValuePillar.EnvironmentalImpact,
    type: TvdTileType.EROI,
    dataKey: 'waterData',
    programKey: 'water',
    guard: { requireAllFalse: ['isPestDivision'] },
  },
  {
    id: eroiIds.ENERGY,
    metricId: eroiIds.ENERGY,
    valuePillar: TvdValuePillar.EnvironmentalImpact,
    type: TvdTileType.EROI,
    dataKey: 'energyData',
    programKey: 'energy',
    guard: { requireAllFalse: ['isPestDivision'] },
  },
  // Singular GHG: visible for multi-region view OR standard non-EU non-AP view.
  // Uses the `isAggregateGhgVisible` compound flag to express OR logic declaratively.
  {
    id: eroiIds.GREENHOUSEGASES,
    metricId: eroiIds.GREENHOUSEGASES,
    valuePillar: TvdValuePillar.EnvironmentalImpact,
    type: TvdTileType.EROI,
    dataKey: 'greenhouseGasData',
    programKey: 'greenhouse-gases',
    guard: {
      requireAllFalse: ['isPestDivision'],
      requireAllTrue: ['isAggregateGhgVisible'],
    },
  },
  // GHG Energy tile: AP, LA or IMEA Institutional only, not EU region, not multi-region
  {
    id: eroiIds.GREENHOUSEGASES_ENERGY,
    metricId: eroiIds.GREENHOUSEGASES_ENERGY,
    valuePillar: TvdValuePillar.EnvironmentalImpact,
    type: TvdTileType.EROI,
    dataKey: 'greenhouseGasEnergyData',
    programKey: 'greenhouse-gases-energy',
    guard: {
      requireAllTrue: ['isAPOrLAOrIMEAInstitutionalEnabled'],
      requireAllFalse: ['isPestDivision', 'isRegionEU', 'isMultipleRegionsSelected'],
    },
  },
  // Pest division: Furniture Preservation Energy
  {
    id: eroiIds.FURNITUREPRESERVATIONENERGY,
    metricId: eroiIds.FURNITUREPRESERVATIONENERGY,
    valuePillar: TvdValuePillar.EnvironmentalImpact,
    type: TvdTileType.EROI,
    dataKey: 'furniturePreservationEnergyData',
    programKey: 'furniture-preservation-energy',
    guard: { requireAllTrue: ['isPestDivision'] },
  },
];

/**
 * Composite tiles for the **Environmental Impact** value pillar.
 *
 * | Composite           | Guard / when rendered                                       |
 * |---------------------|-------------------------------------------------------------|
 * | Solid Waste         | Non-Pest.                                                  |
 * |                     | Aggregates Plastic Waste + Cardboard Waste.                |
 * | Greenhouse Gases EU | EU region only, single region selected, non-Pest.          |
 * |                     | Aggregates GHG Energy + GHG Transport + GHG Packaging.     |
 * |                     | Replaces the singular GHG tile when the user is in EU view.|
 *
 * Why two separate GHG entries (one individual in `EnvironmentalImpactTilesConfig`,
 * one composite here)?  The singular total GHG metric is shown for NA / multi-region
 * views; the EU view breaks it into three sub-components because EU reporting
 * requires the breakdown.  The guards on each ensure only one renders at a time.
 */
export const EnvironmentalImpactCompositeTilesConfig: CompositeTileConfig[] = [
  // Solid Waste composite (non-pest)
  {
    id: eroiIds.SOLID_WASTE,
    valuePillar: TvdValuePillar.EnvironmentalImpact,
    type: TvdTileType.COMPOSITE,
    showInfoTooltip: false,
    guard: { requireAllFalse: ['isPestDivision'] },
    children: [
      {
        id: eroiIds.PLASTIC,
        metricId: eroiIds.PLASTIC,
        valuePillar: TvdValuePillar.EnvironmentalImpact,
        type: TvdTileType.EROI,
        dataKey: 'plasticWasteData',
        programKey: 'plastic-waste',
        isSubComponent: true,
      },
      {
        id: eroiIds.CARDBOARD,
        metricId: eroiIds.CARDBOARD,
        valuePillar: TvdValuePillar.EnvironmentalImpact,
        type: TvdTileType.EROI,
        dataKey: 'cardboardWasteData',
        programKey: 'cardboard-waste',
        isSubComponent: true,
      },
    ],
  },
  // Greenhouse Gases EU composite (EU region, non-pest, single-region only)
  {
    id: eroiIds.GREENHOUSEGASES,
    valuePillar: TvdValuePillar.EnvironmentalImpact,
    type: TvdTileType.COMPOSITE,
    showInfoTooltip: false,
    guard: {
      requireAllTrue: ['isRegionEU'],
      requireAllFalse: ['isPestDivision', 'isMultipleRegionsSelected'],
    },
    children: [
      {
        id: eroiIds.GREENHOUSEGASES_ENERGY,
        metricId: eroiIds.GREENHOUSEGASES_ENERGY,
        valuePillar: TvdValuePillar.EnvironmentalImpact,
        type: TvdTileType.EROI,
        dataKey: 'greenhouseGasEnergyData',
        programKey: 'greenhouse-gases-energy',
        isSubComponent: true,
      },
      {
        id: eroiIds.GREENHOUSEGASES_TRANSPORT,
        metricId: eroiIds.GREENHOUSEGASES_TRANSPORT,
        valuePillar: TvdValuePillar.EnvironmentalImpact,
        type: TvdTileType.EROI,
        dataKey: 'greenhouseGasTransportData',
        programKey: 'greenhouse-gases-transport',
        isSubComponent: true,
      },
      {
        id: eroiIds.GREENHOUSEGASES_PACKAGING,
        metricId: eroiIds.GREENHOUSEGASES_PACKAGING,
        valuePillar: TvdValuePillar.EnvironmentalImpact,
        type: TvdTileType.EROI,
        dataKey: 'greenhouseGasPackagingData',
        programKey: 'greenhouse-gases-packaging',
        isSubComponent: true,
      },
    ],
  },
];
